/**
 * PubNubContext — React provider that manages realtime lifecycle.
 *
 * Initializes Azure Web PubSub when an auth token is available and tears
 * it down on unmount. Wrap the app in <PubNubProvider> (done in react-bridge.tsx).
 *
 * azure-web-pubsub-service.ts guards against double-init so multiple callers
 * (React context, app-compat.ts, login handler) are safe.
 */

import { createContext, useContext, useEffect, useRef } from 'react';
import { initAzureWebPubSub, stopAzureWebPubSub, isInitialized } from '@/services/azure-web-pubsub-service';
import { getAuthObject } from '@/utils/auth';
import { hasValue } from '@/utils/validation';

interface PubNubContextValue {
  /** Whether PubNub is currently initialized and connected */
  initialized: boolean;
}

const PubNubContext = createContext<PubNubContextValue>({ initialized: false });

export function usePubNub(): PubNubContextValue {
  return useContext(PubNubContext);
}

export function PubNubProvider({ children }: { children: React.ReactNode }) {
  const initializedRef = useRef(false);

  useEffect(() => {
    const authObj = getAuthObject();
    if (hasValue(authObj) && !isInitialized()) {
      initAzureWebPubSub(() => {
        initializedRef.current = true;
      });
    }

    return () => {
      // Don't tear down realtime on unmount — legacy code may still need it.
      // stopAzureWebPubSub() will be called explicitly on logout.
    };
  }, []);

  return (
    <PubNubContext.Provider value={{ initialized: initializedRef.current }}>
      {children}
    </PubNubContext.Provider>
  );
}
